/**
 * #2441 — a date the family already knows (a child's date of birth, a
 * diagnosis date), typed as month / day / year instead of picked.
 *
 * The native `<input type="date">` this replaces opened Safari's month-at-a-
 * time calendar on TODAY, so reaching a birth year meant clicking back one
 * month at a time. Typing into it was no safer: Safari's year segment took
 * `222222`, and a two-digit "12" saved as the year 0012.
 *
 * The shape is the GOV.UK "date input" and USWDS "memorable date" pattern:
 *  - Month is a select of month NAMES in the reader's language, so nobody has
 *    to know whether 10/08 means October or August.
 *  - Day and year are text inputs with a numeric keypad (`inputMode`), never
 *    `type="number"` (spinners, scroll-wheel edits, silent `e`).
 *  - No auto-advance between parts: it traps keyboard users correcting a typo.
 *  - The parts follow the locale's own order (M D Y in English, D M Y in
 *    es/pt/fr, Y M D in zh), read from `Intl` rather than hardcoded.
 *
 * The stored value is unchanged: `YYYY-MM-DD`, or null. A part-typed date
 * stores null, and the parts the family typed stay on screen. A full but
 * impossible date (Feb 30, a 2-digit year, a year outside 1900–2100) also
 * stores null and says so beside the control.
 *
 * #2586: because a part-typed date stores null, the step's Save could not
 * tell it from an empty one. The control therefore also reports the sentence
 * it would show for its draft through `DateDraftContext`, so the wizard's
 * Save gate can hold the step and name the problem at the date — for
 * optional dates too, which otherwise advanced and dropped the parts.
 */
import { useEffect, useMemo, useRef, useState, type FocusEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { fmtDateForInput, isPlausibleDateAnswer, MEMORABLE_YEAR_MAX, MEMORABLE_YEAR_MIN } from '@/lib/intakeFields';
import { useDateDraftRegistry } from './dateDrafts';

type Part = 'month' | 'day' | 'year';
interface Draft { month: string; day: string; year: string }

const EMPTY: Draft = { month: '', day: '', year: '' };

function draftFromValue(value: unknown): Draft {
  const iso = fmtDateForInput(value);
  // #2448: a stored date the old calendar let through (year 0012, or a
  // five-digit 20126 that serialises as `+020126-…`) opens blank, not as
  // `+020126` / `NaN`.
  if (!iso || !isPlausibleDateAnswer(iso)) return EMPTY;
  const [y, m, d] = iso.split('-');
  return { year: y, month: String(Number(m)), day: String(Number(d)) };
}

export interface YearRange { min: number; max: number }

type Status =
  | { kind: 'empty' }
  | { kind: 'partial' }
  | { kind: 'invalid' }
  | { kind: 'outOfRange' }
  | { kind: 'valid'; iso: string };

export function draftStatus(draft: Draft, range: YearRange = { min: MEMORABLE_YEAR_MIN, max: MEMORABLE_YEAR_MAX }): Status {
  const { month, day, year } = draft;
  if (!month && !day && !year) return { kind: 'empty' };
  // A year short of four digits is still being typed, not wrong yet.
  if (!month || !day || year.length < 4) return { kind: 'partial' };
  if (!/^\d{4}$/.test(year) || !/^\d{1,2}$/.test(day)) return { kind: 'invalid' };
  const y = Number(year); const m = Number(month); const d = Number(day);
  if (m < 1 || m > 12 || d < 1) return { kind: 'invalid' };
  if (y < range.min || y > range.max) return { kind: 'outOfRange' };
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCMonth() !== m - 1) return { kind: 'invalid' }; // Feb 30 rolls into March
  return { kind: 'valid', iso: `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` };
}

/** The locale's own order of the three parts. */
function partOrder(locale: string): Part[] {
  try {
    const order = new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'numeric', day: 'numeric', timeZone: 'UTC' })
      .formatToParts(new Date(Date.UTC(2000, 10, 22)))
      .map((p) => p.type)
      .filter((t): t is Part => t === 'month' || t === 'day' || t === 'year');
    if (order.length === 3) return order;
  } catch { /* an unknown locale tag: fall through */ }
  return ['month', 'day', 'year'];
}

const PART_FALLBACK: Record<Part, string> = { month: 'Month', day: 'Day', year: 'Year' };

/** "Month" / "Day" / "Year" in the reader's language, from the CLDR data the
 *  browser already ships — no catalog key to translate and judge. */
function partLabel(locale: string, part: Part): string {
  try {
    const DisplayNames = (Intl as unknown as { DisplayNames?: new (l: string, o: { type: string }) => { of(code: string): string | undefined } }).DisplayNames;
    const name = DisplayNames ? new DisplayNames(locale, { type: 'dateTimeField' }).of(part) : undefined;
    if (name) return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1);
  } catch { /* older engines lack the dateTimeField type */ }
  return PART_FALLBACK[part];
}

function monthNames(locale: string): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' });
  return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(Date.UTC(2000, i, 1))));
}

const digits = (s: string, max: number) => s.replace(/\D/g, '').slice(0, max);

const SELECT_CLASS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-0 text-body ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

interface Props {
  /** The id the field's label points at; the FIRST part in locale order carries it. */
  id: string;
  /** The field key, for test and automation hooks (`data-date-field`). */
  fieldKey: string;
  value: unknown;
  onChange: (value: string | null) => void;
  onBlur?: () => void;
  ariaRequired?: boolean;
  /** The years this date may fall in. Defaults to the 1900–2100 typo guard;
   *  the child's date of birth narrows it to the program's 0–18 age band. */
  yearRange?: YearRange;
  /** #2448: the year an empty date starts with (the birth year a screened age
   *  implies). Only seeds the year box; the stored value stays null until the
   *  family picks a month and day. */
  suggestedYear?: number | null;
  /** #2642: the owning surface flags this date (its Save gate shows the
   *  marker). Every part then carries aria-invalid and, with `errorId`,
   *  points at that marker, as every other field type does. */
  invalid?: boolean;
  /** The id of the surface's own error text for this field. */
  errorId?: string;
}

export function MemorableDateInput({ id, fieldKey, value, onChange, onBlur, ariaRequired, yearRange, suggestedYear, invalid, errorId: surfaceErrorId }: Props) {
  const { t, i18n } = useTranslation('validation');
  const locale = i18n.resolvedLanguage || i18n.language || 'en';
  const order = useMemo(() => partOrder(locale), [locale]);
  const months = useMemo(() => monthNames(locale), [locale]);

  const [draft, setDraft] = useState<Draft>(() => {
    const seeded = draftFromValue(value);
    return seeded === EMPTY && suggestedYear ? { ...EMPTY, year: String(suggestedYear) } : seeded;
  });
  const [touched, setTouched] = useState(false);
  const groupRef = useRef<HTMLDivElement>(null);
  const rMin = yearRange?.min ?? MEMORABLE_YEAR_MIN;
  const rMax = yearRange?.max ?? MEMORABLE_YEAR_MAX;
  const range = { min: rMin, max: rMax };
  const statusOf = (d: Draft) => draftStatus(d, range);

  // Re-seed from the stored value only when it differs from what the draft
  // already says. A part-typed draft stores null, and that null must not wipe
  // the parts the family is halfway through typing.
  const stored = fmtDateForInput(value);
  useEffect(() => {
    setDraft((cur) => {
      const s = draftStatus(cur, { min: rMin, max: rMax });
      const mine = s.kind === 'valid' ? s.iso : '';
      return mine === stored ? cur : (stored ? draftFromValue(stored) : (s.kind === 'valid' ? EMPTY : cur));
    });
  }, [stored, rMin, rMax]);

  const update = (part: Part, raw: string) => {
    const next = { ...draft, [part]: part === 'month' ? raw : digits(raw, part === 'year' ? 4 : 2) };
    setDraft(next);
    const s = statusOf(next);
    const iso = s.kind === 'valid' ? s.iso : null;
    if ((iso ?? '') !== stored) onChange(iso);
  };

  // The group blurs as one control: leaving month for day is not a blur.
  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (groupRef.current?.contains(e.relatedTarget as Node | null)) return;
    setTouched(true);
    onBlur?.();
  };

  // Spoken only once the family leaves the control: never mid-typing, and
  // never at a stored value staff opened without touching it.
  const status = statusOf(draft);
  const hasProblem = status.kind === 'invalid' || status.kind === 'outOfRange' || status.kind === 'partial';
  // A short or out-of-window year is the likeliest slip ("12" for 2012), so
  // the message names the years rather than calling the whole date wrong.
  const yearProblem = status.kind === 'outOfRange' || (draft.year !== '' && draft.year.length < 4);
  const message = yearProblem ? t('date.yearBetween', { min: range.min, max: range.max }) : t('date.invalid');
  // #2586: tell the owning surface what this draft would say, so its Save
  // gate can hold the step on a half-typed date. Cleared when the control
  // leaves the screen.
  const registry = useDateDraftRegistry();
  const reported = hasProblem ? message : null;
  useEffect(() => {
    registry?.report(fieldKey, reported);
  }, [registry, fieldKey, reported]);
  useEffect(() => () => { registry?.report(fieldKey, null); }, [registry, fieldKey]);
  // While the surface shows its own marker for this field, the control stays
  // quiet rather than saying the same sentence twice.
  const showError = touched && hasProblem && !(registry?.flagged(fieldKey) ?? false);
  const errorId = `${id}-error`;
  const flaggedBySurface = Boolean(invalid);
  const describedBy = [flaggedBySurface ? surfaceErrorId : undefined, showError ? errorId : undefined]
    .filter(Boolean)
    .join(' ') || undefined;
  const partId = (part: Part) => (part === order[0] ? id : `${id}-${part}`);

  const control = (part: Part) => {
    const common = {
      id: partId(part),
      'data-date-part': part,
      'aria-required': ariaRequired,
      'aria-invalid': showError || flaggedBySurface || undefined,
      'aria-describedby': describedBy,
    };
    if (part === 'month') {
      return (
        <select {...common} value={draft.month} onChange={(e) => update('month', e.target.value)} className={SELECT_CLASS}>
          <option value="" />
          {months.map((name, i) => <option key={name} value={String(i + 1)}>{name}</option>)}
        </select>
      );
    }
    return (
      <Input
        {...common}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        maxLength={part === 'year' ? 4 : 2}
        value={draft[part]}
        onChange={(e) => update(part, e.target.value)}
      />
    );
  };

  return (
    <div ref={groupRef} role="group" data-date-field={fieldKey} onBlur={handleBlur}>
      <div className="flex max-w-sm gap-3">
        {order.map((part) => (
          <div key={part} className={cn(part === 'month' ? 'min-w-0 flex-1' : part === 'year' ? 'w-24 shrink-0' : 'w-16 shrink-0')}>
            <label htmlFor={partId(part)} className="mb-1 block text-caption text-stevens-ink-3">
              {partLabel(locale, part)}
            </label>
            {control(part)}
          </div>
        ))}
      </div>
      {showError && (
        <p id={errorId} className="mt-1.5 text-caption text-status-danger-fg">{message}</p>
      )}
    </div>
  );
}
